// src/pages/team/RoleFrameworkPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// BRIO Role Framework — three-tab page (CSV-driven, graded competencies)
//
// Tab A: Overview       — career path diagram + grouped expandable role cards
// Tab B: Competency Matrix — sticky-header table with graded indicators + legend
// Tab C: My Role        — personalised view for the current logged-in user
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, User } from 'lucide-react';
import {
  BrioFrame,
  BrioAnalyticsSurface,
} from '../../components/BrioComponents';
import CompetencyLevelIndicator, { GRADE_LABELS } from '../../components/CompetencyLevelIndicator';
import useRoleFramework from '../../hooks/useRoleFramework';

// ─────────────────────────────────────────────────────────────────────────────
// Shared style constants
// ─────────────────────────────────────────────────────────────────────────────

const FONT = 'Raleway, sans-serif';

// Display name overrides — CSV uses canonical names, UI can show shortened versions
const ROLE_DISPLAY_NAMES = {};
const displayName = (name) => ROLE_DISPLAY_NAMES[name] || name;

// Role introductions — keyed by roleName
// Entries can be a plain string (legacy) or a structured object { tagline, description, holds[] }
const ROLE_INTRODUCTIONS = {
  'Intern': 'Interns are early-stage architecture students who contribute to projects while developing core professional skills. They support design teams through drafting, modelling and visualisation tasks while learning studio workflows, documentation standards and collaborative design processes. The role focuses on learning, exposure to practice and building foundational architectural skills.',
  'Junior Architect': {
    tagline: 'Builds accuracy through disciplined participation',
    description: 'The Junior Architect enters the practice at the point where formal training meets the complexity of live projects. The primary function of this role is not yet contribution in the strategic sense — it is calibration: learning how a practice coordinates itself, how drawings carry consequence, and how individual output fits within a collective act. Performance at this level is measured by attentiveness, accuracy, and the willingness to revise.',
    holds: [
      { title: 'Executes defined tasks with accuracy', text: 'Produces drawings, models, and documentation to the standard and scope set by the project team. Output is checked and directed by others; the emphasis is on precision within a defined brief, not independent judgement.' },
      { title: 'Develops rigour in process', text: 'Builds working habits — file discipline, naming conventions, annotation standards — that make individual output legible and dependable within a team environment.' },
      { title: 'Begins to read across the project', text: 'Develops the ability to understand how individual tasks relate to broader project decisions. This is the start of contextual awareness, not yet its full exercise.' },
    ],
  },
  'Medior Architect': {
    tagline: 'Produces reliable, self-directed work within a structured project',
    description: 'The Medior Architect has moved beyond the learning phase. Work is no longer primarily about absorbing process — it is about applying it consistently. At this level, output is expected to be internally resolved: coordinated, considered, and requiring only light correction. The role also begins to carry a limited coordination function, engaging with information flows and supporting the legibility of work for others.',
    holds: [
      { title: 'Delivers resolved, self-directed work', text: 'Produces packages of work that are complete in themselves — coordinated across disciplines, consistent with project intent, and requiring minimal correction before use.' },
      { title: 'Manages information and communication', text: 'Engages with consultants and internal team members to ensure that information is current, dependencies are flagged, and coordination gaps are addressed before they become problems.' },
      { title: 'Supports and checks junior output', text: 'Takes limited responsibility for reviewing the work of Junior Architects — not as a line management function, but as part of maintaining overall project quality.' },
    ],
  },
  'Project Architect': {
    tagline: 'Holds and delivers projects with operational independence',
    description: 'The Project Architect is the operational engine of a project — and on smaller projects, the sole engine. This role carries the capacity to independently lead a project from start to finish without requiring a Senior Architect: structuring the work, coordinating across consultants and clients, and delivering output to the required standard. On larger or more complex projects, the Project Architect holds the operational framework while others carry specific technical or design responsibilities. In both contexts, the accountability for how the project runs sits here.',
    holds: [
      { title: 'Leads small projects independently', text: 'Has the capacity to carry a project — from brief through to delivery — without Senior Architect support. This includes design resolution, consultant coordination, and client communication on projects of appropriate scope and complexity.' },
      { title: 'Structures and sequences the project', text: 'Defines the working methodology for a project: what needs to be produced, by whom, in what order, and to what level of coordination. Maintains this structure across the full project lifecycle.' },
      { title: 'Coordinates across all stakeholders', text: 'Manages the flow of information between the design team, consultants, contractors, and clients. Ensures that decisions are documented, dependencies are tracked, and nothing falls between parties.' },
      { title: 'Maintains delivery integrity', text: 'Takes direct accountability for the completeness, coordination, and timely release of project outputs. Identifies risks early and acts to resolve them before they affect delivery.' },
    ],
  },
  'Senior Architect': {
    tagline: 'Leads teams and holds sole responsibility across multiple projects',
    description: 'The Senior Architect operates as a team leader and multi-project holder. This role does not sit within a project structure defined by others — it defines and leads that structure directly. A Senior Architect carries sole responsibility for the outcomes of their projects while simultaneously leading the team working on them. Where a Project Architect runs one project with defined support, the Senior Architect runs multiple projects and the people delivering them, making consequential decisions independently across all fronts.',
    holds: [
      { title: 'Leads a team across multiple projects', text: 'Holds direct leadership responsibility for a team of architects working across a portfolio of projects simultaneously. Sets the working standards, distributes responsibility, and ensures the team functions with clarity and purpose.' },
      { title: 'Bears sole accountability for project outcomes', text: 'Is the primary point of responsibility for the quality, coordination, and delivery of every project within their portfolio. Does not share or distribute this accountability — it rests here.' },
      { title: 'Manages project scheduling and budgeting', text: 'Owns the programme and budget for each project in their portfolio. Sets realistic schedules, tracks expenditure against fee, identifies variances early, and makes the adjustments needed to keep projects on track without escalating to the Associate.' },
      { title: 'Resolves complexity across design and delivery', text: 'Brings independent judgement to technically demanding and design-critical decisions. Resolves problems at the intersection of design intent, technical rigour, and buildability without requiring escalation.' },
      { title: 'Develops the capability of the team', text: 'Actively builds the skills and confidence of team members through direct engagement with their work — not as a peripheral mentoring function, but as an integral part of leading the team.' },
    ],
  },
  'Associate': {
    tagline: 'Oversees Senior Architects, mentors across the office, and drives operational efficiency',
    description: 'The Associate operates both within and across the project structure. At the point of entry into this role, the Associate continues to perform the functions of a Senior Architect — leading projects, managing teams, and resolving complexity — but with a broader field of view.\n\nThis expanded position allows the Associate to hold project leadership alongside a growing responsibility for alignment across projects, teams, and the studio as a whole. The role is therefore defined not by stepping away from project delivery, but by holding it while simultaneously shaping how projects relate to one another.\n\nOver time, the emphasis of the role shifts further toward operating across projects — but remains grounded in delivery, with the capacity to step into and lead projects directly when required.',
    holds: [
      { title: 'Leads projects with a widened field of view', text: 'Continues to take on project leadership responsibilities similar to a Senior Architect — leading teams, making decisions, and resolving complexity — but with an increased awareness of how those decisions relate to other projects, teams, and the overall capacity of the studio.' },
      { title: 'Oversees Senior Architects and their teams', text: 'Holds oversight of multiple Senior Architects simultaneously — monitoring the health of their projects, the performance of their teams, and the consistency of output across the office. Intervenes where standards are at risk and creates the conditions for Senior Architects to lead effectively.' },
      { title: 'Mentors across all levels of the studio', text: 'Takes an active, office-wide role in developing people — from Junior Architects through to Senior Architects. Identifies capability gaps, creates learning opportunities, and builds a culture of continuous improvement that extends beyond any individual team.' },
      { title: 'Sits on project negotiations', text: 'Is present in negotiations with clients and contractors — on scope, programme, fees, and deliverables — as an informed participant rather than the primary lead. Brings cross-project context and operational awareness to these conversations, ensuring that what is agreed is consistent with the studio\'s broader capacity and commitments.' },
      { title: 'Holds fee visibility across projects to allocate people effectively', text: 'Maintains an overview of fee allocation across the full project portfolio. Uses this visibility to make informed decisions about where people are deployed — moving team members between projects when capacity, programme, or budget shifts require it — ensuring the studio\'s resources are distributed where they are most needed.' },
    ],
  },
  'Business Associate': {
    tagline: 'Builds the conditions for future work',
    description: 'The Business Associate operates in the space between current work and future work. This role is not defined by project delivery — it is defined by pipeline: building the relationships, identifying the opportunities, and making the judgements that determine what work the studio will take on next. It requires strategic clarity about where the studio is positioned and where it should be heading, combined with the relational intelligence to make that positioning real.',
    holds: [
      { title: 'Builds and sustains the project pipeline', text: 'Develops relationships and opportunities that translate into future work. Understands the studio\'s capacity, strategic priorities, and design ambitions well enough to identify the right opportunities — not just any opportunity.' },
      { title: 'Manages long-term client and collaborator relationships', text: 'Establishes trust with clients, developers, institutions, and collaborators over time. Ensures that the studio\'s external reputation is consistent with its internal values and quality standards.' },
      { title: 'Aligns incoming work with studio direction', text: 'Evaluates potential projects not only on commercial viability but on strategic and design fit. Acts as the first filter for what the studio commits to, in alignment with Principals.' },
    ],
  },
  'Partner / Director': {
    tagline: 'Holds the long-term coherence and direction of the practice',
    description: 'The Partner / Director is responsible for the practice as a whole — its design quality, its operational health, its cultural identity, and its long-term trajectory. This role extends beyond any individual project. Principals lead across one or more domains (design, delivery, or business) while collectively holding the decisions that define what the studio is and where it is going. The scope of accountability at this level is without ceiling.',
    holds: [
      { title: 'Leads a domain and contributes to the whole', text: 'Takes primary responsibility for design, delivery, or business development at a practice level — while contributing to collective decisions that shape all three. Domain leadership does not mean domain isolation.' },
      { title: 'Defines the strategic and cultural direction of the studio', text: 'Makes — or significantly influences — decisions about positioning, typology, growth, quality standards, and the kind of practice BRIO is becoming. These are decisions with multi-year consequences.' },
      { title: 'Resolves situations of ambiguity, conflict, or risk', text: 'Acts at moments when clarity is absent, when competing interests must be arbitrated, or when the stakes of a decision are high enough to require the most senior accountability in the room.' },
    ],
  },
};

// Per-sublevel introductions — keyed by roleSublevel
// Structured the same as ROLE_INTRODUCTIONS: { tagline, description, holds[] }
const SUBLEVEL_INTRODUCTIONS = {
  'Associate L10': {
    tagline: 'Project-Embedded Associate',
    description: 'Operates within projects while beginning to develop awareness beyond them',
    holds: [
      { title: 'Leads projects independently', text: 'Leads projects at the level of a Senior Architect — managing teams, making decisions, and resolving complexity — while beginning to recognise how those decisions may affect adjacent timelines and teams.' },
      { title: 'Develops awareness beyond the project', text: 'Begins to observe how other projects are structured and delivered, building an early understanding of interdependencies across the studio.' },
      { title: 'Supports team development within the project', text: 'Engages with Junior and Medior Architects within the team — reviewing work, guiding output, and contributing to consistency.' },
      { title: 'Observes project negotiations', text: 'Attends negotiations selectively to understand how scope, programme, and fees are structured and discussed.' },
      { title: 'Builds early understanding of resource and fee relationships', text: 'Begins to recognise how team size, time allocation, and project scope relate to fee structures.' },
    ],
  },
  'Associate L11': {
    tagline: 'Emerging Cross-Project Awareness',
    description: 'Balances project leadership with early cross-project engagement',
    holds: [
      { title: 'Leads projects with cross-project awareness', text: 'Leads projects while beginning to factor in the pressures and constraints of other projects — including shared resources and overlapping timelines.' },
      { title: 'Begins light-touch oversight across projects', text: 'Tracks selected projects led by Senior Architects — identifying risks or inconsistencies and raising them where appropriate.' },
      { title: 'Extends mentorship beyond the immediate team', text: 'Supports Junior and Medior Architects across projects, contributing to broader team development.' },
      { title: 'Contributes to project negotiations', text: 'Participates in negotiations as a supporting voice — bringing clarity from project experience while beginning to understand cross-project implications.' },
      { title: 'Develops awareness of fee and staffing balance', text: 'Recognises when project demands and staffing may be misaligned, though does not yet act independently to resolve it.' },
    ],
  },
  'Associate L12': {
    tagline: 'Active Cross-Project Operator',
    description: 'Actively aligns projects, teams, and delivery across the office',
    holds: [
      { title: 'Leads projects while balancing multiple demands', text: 'Makes project-level decisions with a clear understanding of their impact on team capacity and other ongoing work.' },
      { title: 'Actively supports and calibrates Senior-led projects', text: 'Engages with multiple projects — reviewing progress, supporting decisions, and intervening where coordination or quality is at risk.' },
      { title: 'Mentors across teams, including Senior Architects (lightly)', text: 'Supports consistency in how teams operate and begins influencing how Senior Architects lead their teams.' },
      { title: 'Participates consistently in negotiations', text: 'Brings informed input into negotiations — considering both project-specific and cross-project implications.' },
      { title: 'Understands and informs resource allocation', text: 'Has a working understanding of how fee, staffing, and delivery interact — and contributes to decisions around team allocation.' },
    ],
  },
  'Associate L13': {
    tagline: 'Operational Integrator',
    description: 'Integrates delivery, people, and resources across the studio',
    holds: [
      { title: 'Leads selectively while prioritising across projects', text: 'Steps into projects where required, but primarily operates by ensuring alignment across multiple projects.' },
      { title: 'Oversees Senior Architects and ensures consistency across teams', text: 'Maintains consistent oversight across teams — ensuring quality, pace, and working methods are aligned across the office.' },
      { title: 'Drives structured mentorship across the studio', text: 'Actively identifies capability gaps and strengthens performance across teams through deliberate mentoring.' },
      { title: 'Influences outcomes in project negotiations', text: 'Plays an active role in shaping negotiation outcomes — ensuring commitments align with studio capacity and priorities.' },
      { title: 'Actively reallocates resources across projects', text: 'Uses fee visibility and project understanding to move people across teams and balance workload effectively.' },
    ],
  },
  'Associate L14': {
    tagline: 'Full Associate',
    description: 'Holds the system fluidly and without friction',
    holds: [
      { title: 'Moves fluidly between project leadership and oversight', text: 'Leads projects where required while maintaining a continuous awareness of how all projects relate to each other.' },
      { title: 'Oversees multiple teams without disrupting autonomy', text: 'Maintains standards across the office while allowing Senior Architects to lead effectively within their scope.' },
      { title: 'Shapes the development culture of the studio', text: 'Builds capability across all levels through consistent, intentional engagement.' },
      { title: 'Brings clarity and authority to negotiations', text: 'Balances design ambition with operational reality — ensuring decisions are both ambitious and grounded.' },
      { title: 'Optimises resource allocation across the portfolio', text: 'Continuously aligns people, time, and fees across projects — maintaining both delivery quality and studio health.' },
    ],
  },
};

// Role introduction renderer — handles plain strings (legacy) and structured objects
const RoleIntroduction = ({ intro, compact }) => {
  if (!intro) return null;
  // Legacy plain string
  if (typeof intro === 'string') {
    return (
      <p style={{
        fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7,
        margin: compact ? '12px 0 0' : '0 0 4px', padding: compact ? 0 : '0 2px',
      }}>
        {intro}
      </p>
    );
  }
  // Structured object { tagline, description, holds[] }
  return (
    <div style={{
      margin: compact ? 'var(--brio-space-xs) 0 0' : '0 0 4px',
      padding: compact ? 0 : '0 2px',
    }}>
      {intro.tagline && (
        <div style={{
          fontFamily: 'var(--brio-font-family)', fontSize: 'var(--brio-font-size-m)',
          fontWeight: 600, fontStyle: 'italic',
          color: 'var(--brio-gold)', lineHeight: 1.5,
          marginBottom: 'var(--brio-space-xs)',
        }}>
          {intro.tagline}
        </div>
      )}
      {intro.description && intro.description.split('\n\n').map((para, i) => (
        <p key={i} style={{
          fontFamily: 'var(--brio-font-family)', fontSize: 'var(--brio-font-size-base)',
          color: 'var(--brio-grey)', lineHeight: 1.7,
          margin: i === 0 ? '0 0 var(--brio-space-xs)' : 'var(--brio-space-xs) 0',
        }}>
          {para}
        </p>
      ))}
      {intro.holds && intro.holds.length > 0 && (
        <div style={{ marginTop: 'var(--brio-space-s)' }}>
          <div style={{
            fontSize: 'var(--brio-font-size-xs)', fontWeight: 700,
            letterSpacing: '1.2px', textTransform: 'uppercase',
            color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)',
            marginBottom: 'var(--brio-space-xs)',
          }}>
            What this role holds
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {intro.holds.map((item, i) => (
              <div key={i} style={{
                padding: 'var(--brio-space-xs) 0',
                borderBottom: '1px solid var(--brio-border)',
              }}>
                <div style={{
                  fontFamily: 'var(--brio-font-family)', fontSize: 'var(--brio-font-size-m)',
                  fontWeight: 600, color: 'var(--brio-black)',
                  marginBottom: 2,
                }}>
                  {item.title}
                </div>
                <div style={{
                  fontFamily: 'var(--brio-font-family)', fontSize: 'var(--brio-font-size-m)',
                  color: 'var(--brio-grey)', lineHeight: 1.6,
                }}>
                  {item.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Section divider label
const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 'var(--brio-font-size-xs)', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase',
    color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)',
    padding: 'var(--brio-space-xs) 0 4px',
    borderBottom: '1px solid var(--brio-border)',
    marginBottom: 'var(--brio-space-xs)',
  }}>
    {children}
  </div>
);

// Role badge chip — circular code chip with pill-shaped badge
const RoleBadge = ({ roleCode, roleName, sublevel, small, roleStyle }) => {
  const s = roleStyle[roleCode] || roleStyle.A;
  return (
    <span className="brio-badge" style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: small ? '2px 8px' : '3px 10px',
      borderRadius: 20,
      background: s.bg,
      border: `1px solid ${s.border}`,
      color: s.text,
      fontSize: small ? 'var(--brio-font-size-s)' : 'var(--brio-font-size-m)',
      fontFamily: 'var(--brio-font-family)', fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: small ? 16 : 18, height: small ? 16 : 18,
        borderRadius: '50%',
        background: s.chip, color: '#fff',
        fontSize: small ? 'var(--brio-font-size-xs)' : 'var(--brio-font-size-s)',
        fontWeight: 700,
      }}>{roleCode}</span>
      {displayName(roleName)}{sublevel ? ` · ${displayName(sublevel)}` : ''}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Competency Legend
// ─────────────────────────────────────────────────────────────────────────────

function CompetencyLegend() {
  return (
    <div style={{
      display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
      padding: '8px 12px',
      background: 'rgba(0,0,0,0.02)',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 6,
      marginBottom: 12,
    }}>
      <span style={{ fontSize: 10, color: '#888', fontFamily: FONT, fontWeight: 600 }}>Legend:</span>
      {GRADE_LABELS.map((label, grade) => (
        <span key={grade} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontFamily: FONT, color: '#555' }}>
          <CompetencyLevelIndicator grade={grade} size="small" showTooltip={false} />
          <span>{grade} — {label}</span>
        </span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sublevel detail card (used in Overview and My Role)
// ─────────────────────────────────────────────────────────────────────────────

function SublevelCard({ row, highlight, label, competencyGroups, roleStyle }) {
  const s = roleStyle[row.roleCode] || roleStyle.A;
  const [showComps, setShowComps] = useState(false);

  const infoRow = (text) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4 }}>
      <span style={{ color: s.chip, fontSize: 11, flexShrink: 0, marginTop: 1 }}>◈</span>
      <span style={{ fontSize: 11, color: '#444', fontFamily: FONT, lineHeight: 1.5 }}>{text}</span>
    </div>
  );

  return (
    <div style={{
      border: `1px solid ${highlight ? s.chip : 'var(--brio-border, rgba(0,0,0,0.1))'}`,
      borderRadius: 8,
      background: highlight ? s.bg : '#fff',
      padding: '14px 16px',
      boxShadow: highlight ? `0 0 0 2px ${s.chip}22` : 'none',
      transition: 'box-shadow 0.15s',
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <RoleBadge roleCode={row.roleCode} roleName={row.roleName} sublevel={row.roleSublevel} roleStyle={roleStyle} />
            {label && (
              <span style={{
                fontSize: 10, fontWeight: 500, color: '#777',
                background: 'rgba(0,0,0,0.04)', padding: '2px 8px', borderRadius: 10,
                fontFamily: FONT,
              }}>
                {label}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#777', fontFamily: FONT }}>
            {row.experienceRange ? `${row.experienceRange} yrs` : ''}
            {row.bonusEligible ? ' · Bonus eligible' : ''}
          </div>
        </div>
      </div>

      {/* Sublevel introduction (if available) */}
      {SUBLEVEL_INTRODUCTIONS[row.roleSublevel] && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: s.text, marginBottom: 4 }}>
            {SUBLEVEL_INTRODUCTIONS[row.roleSublevel].tagline}
          </div>
          <p style={{ fontSize: 11, color: '#555', fontFamily: FONT, lineHeight: 1.6, margin: '0 0 10px', fontStyle: 'italic' }}>
            {SUBLEVEL_INTRODUCTIONS[row.roleSublevel].description}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SUBLEVEL_INTRODUCTIONS[row.roleSublevel].holds.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <span style={{ color: s.chip, fontSize: 11, flexShrink: 0, marginTop: 1 }}>◈</span>
                <span style={{ fontSize: 11, color: '#444', fontFamily: FONT, lineHeight: 1.5 }}>
                  <strong>{h.title}</strong> — {h.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick facts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '4px 16px', marginBottom: 12 }}>
        {infoRow(`Scope: ${row.projectScope}`)}
        {infoRow(`Complexity: ${row.projectComplexity}`)}
        {infoRow(`Project lead: ${row.projectLead ? 'Yes' : 'No'}`)}
        {infoRow(`Client contact: ${row.clientInteraction}`)}
        {row.hiringInvolvement && infoRow(`Hiring input: ${row.hiringScope}`)}
      </div>

      {/* Responsibilities */}
      <SectionLabel>Core responsibilities</SectionLabel>
      <p style={{ fontSize: 11, color: '#333', fontFamily: FONT, lineHeight: 1.6, margin: '0 0 10px' }}>
        {row.coreResponsibilities}
      </p>

      {/* Growth focus */}
      <SectionLabel>Growth focus</SectionLabel>
      <p style={{ fontSize: 11, color: '#555', fontFamily: FONT, lineHeight: 1.6, margin: '0 0 10px', fontStyle: 'italic' }}>
        {row.growthFocus}
      </p>

      {/* Competencies toggle */}
      <button
        onClick={() => setShowComps(v => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, color: s.text, fontFamily: FONT, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 4, padding: 0,
        }}
      >
        {showComps ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {showComps ? 'Hide competencies' : 'Show competencies'}
      </button>

      {showComps && (
        <div style={{ marginTop: 10 }}>
          {competencyGroups.map(group => (
            <div key={group.name} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>
                {group.name}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {group.competencies.map(comp => {
                  const grade = row.competencies[comp.key] || 0;
                  return (
                    <span key={comp.key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 10, fontFamily: FONT,
                      padding: '3px 8px', borderRadius: 10,
                      background: grade > 0 ? s.bg : 'rgba(0,0,0,0.03)',
                      border: `1px solid ${grade > 0 ? s.border : 'rgba(0,0,0,0.08)'}`,
                      color: grade > 0 ? s.text : '#aaa',
                    }}>
                      <CompetencyLevelIndicator grade={grade} size="small" />
                      {comp.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Career Path Diagram (horizontal, used in Overview)
// ─────────────────────────────────────────────────────────────────────────────

function CareerPathDiagram({ roleGroups, roleStyle }) {
  return (
    <BrioAnalyticsSurface padding="m">
      <div style={{ marginBottom: 8, fontSize: 'var(--brio-font-size-m)', color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)' }}>
        Career progression path
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 0', padding: '4px 0' }}>
        {roleGroups.map((group, idx) => {
          const s = roleStyle[group.roleCode] || roleStyle.A;
          const subCount = group.rows.length;
          // Condense experience range: "2 - 3 – 6 - 8" → "2-8"
          const firstExp = group.rows[0].experienceRange;
          const lastExp = group.rows[subCount - 1].experienceRange;
          const startYr = firstExp.split(/\s*-\s*/)[0];
          const endYr = lastExp.split(/\s*-\s*/).pop();
          const expLabel = subCount === 1
            ? `${firstExp} yrs`
            : (startYr === endYr ? `${startYr} yrs` : `${startYr}-${endYr} yrs`);
          return (
            <React.Fragment key={group.roleCode}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: s.chip, border: `2px solid ${s.chip}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--brio-font-family)', fontWeight: 700, fontSize: 14, color: '#fff',
                }}>
                  {group.roleCode}
                </div>
                <div style={{ fontSize: 10, fontFamily: 'var(--brio-font-family)', fontWeight: 600, color: s.text, textAlign: 'center', maxWidth: 72 }}>
                  {displayName(group.roleName)}
                </div>
                <div style={{ fontSize: 8, color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)', textAlign: 'center' }}>
                  {subCount === 1 ? '1 level' : `${subCount} levels`}
                  {' · '}{expLabel}
                </div>
              </div>
              {idx < roleGroups.length - 1 && (
                <div style={{
                  width: 12, height: 2,
                  background: `${s.chip}55`,
                  marginBottom: 30,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </BrioAnalyticsSurface>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab A: Overview — career path + roles grouped, sublevels expandable
// ─────────────────────────────────────────────────────────────────────────────

function OverviewTab({ roleGroups, competencyGroups, roleStyle }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (code) => setExpanded(prev => ({ ...prev, [code]: !prev[code] }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <CareerPathDiagram roleGroups={roleGroups} roleStyle={roleStyle} />

      {roleGroups.map(group => {
        const s = roleStyle[group.roleCode] || roleStyle.A;
        const isOpen = expanded[group.roleCode];
        return (
          <BrioAnalyticsSurface key={group.roleCode} padding="m">
            <div
              onClick={() => toggle(group.roleCode)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--brio-space-xs)', userSelect: 'none' }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: '50%',
                background: s.chip, color: '#fff',
                fontFamily: 'var(--brio-font-family)', fontSize: 14, fontWeight: 700, flexShrink: 0,
              }}>
                {group.roleCode}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--brio-font-family)', fontWeight: 500, fontSize: 'var(--brio-font-size-l)', color: 'var(--brio-black)' }}>
                  {displayName(group.roleName)}
                </div>
                <div style={{ fontSize: 'var(--brio-font-size-m)', color: 'var(--brio-grey)', fontFamily: 'var(--brio-font-family)' }}>
                  {group.rows.length === 1 ? displayName(group.roleName) : `${group.rows.length} sublevels`}
                  {' · '}
                  {group.rows[0].experienceRange}
                  {group.rows.length > 1 ? ` – ${group.rows[group.rows.length - 1].experienceRange} yrs` : ' yrs'}
                </div>
              </div>
              {isOpen ? <ChevronDown size={14} color="var(--brio-grey)" /> : <ChevronRight size={14} color="var(--brio-grey)" />}
            </div>

            {isOpen && (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <RoleIntroduction intro={ROLE_INTRODUCTIONS[group.roleName]} />
                {group.rows.map(row => (
                  <SublevelCard key={row.roleSort} row={row} competencyGroups={competencyGroups} roleStyle={roleStyle} />
                ))}
              </div>
            )}
          </BrioAnalyticsSurface>
        );
      })}

      {/* General introduction */}
      <BrioAnalyticsSurface padding="m">
        <p style={{ fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7, margin: '0 0 10px' }}>
          At BRIO, roles are defined by the scope of responsibility they hold, the degree of independence with which they operate, and the clarity they are able to bring to others. They are positions within a system — each with a distinct centre of gravity — rather than rungs on a ladder.
        </p>
        <p style={{ fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7, margin: '0 0 10px' }}>
          Progression is measured not by time served but by demonstrable capacity: the ability to move from executing tasks under direction, to structuring work independently, to defining the conditions under which others can operate. Each step demands a qualitative shift, not an accumulation.
        </p>
        <p style={{ fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7, margin: '0 0 10px' }}>
          The studio operates across three overlapping domains — design, delivery, and business — rather than within rigid departmental verticals. At the level of Principal / Partner, these domains are led collectively, with responsibility distributed rather than centralised.
        </p>
        <p style={{ fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7, margin: 0 }}>
          This document defines each role with precision. It states what each position holds, how it operates, and where its boundaries lie. It is intended to create clarity without rigidity — and alignment without over-definition.
        </p>
      </BrioAnalyticsSurface>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab B: Competency Matrix — graded indicators, legend, sticky headers
// ─────────────────────────────────────────────────────────────────────────────

function CompetencyMatrixTab({ rows, competencyGroups, roleStyle }) {
  const columns = rows; // already sorted by roleSort

  const headerCell = (bg = '#f5f5f5', color = '#333', borderLeft = false) => ({
    padding: '6px 8px',
    background: bg,
    color,
    fontFamily: FONT,
    fontSize: 10,
    fontWeight: 600,
    textAlign: 'center',
    verticalAlign: 'bottom',
    whiteSpace: 'nowrap',
    borderLeft: borderLeft ? '2px solid rgba(0,0,0,0.08)' : '1px solid rgba(0,0,0,0.06)',
    borderBottom: '2px solid rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    minWidth: 68,
  });

  const labelCell = {
    padding: '5px 10px',
    fontFamily: FONT,
    fontSize: 10,
    color: '#444',
    background: '#fff',
    position: 'sticky',
    left: 0,
    zIndex: 1,
    borderRight: '1px solid rgba(0,0,0,0.1)',
    whiteSpace: 'nowrap',
    minWidth: 180,
  };

  const groupLabelCell = {
    ...labelCell,
    fontWeight: 700,
    fontSize: 9,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#888',
    background: '#f9f9f7',
    paddingTop: 10,
  };

  let lastCode = null;

  return (
    <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={{ ...headerCell('#fff'), textAlign: 'left', position: 'sticky', left: 0, zIndex: 3, minWidth: 180 }}>
              Competency
            </th>
            {columns.map(col => {
              const s = roleStyle[col.roleCode] || roleStyle.A;
              const isNewCode = col.roleCode !== lastCode;
              if (isNewCode) lastCode = col.roleCode;
              return (
                <th key={col.roleSort} style={headerCell(s.bg, s.text, isNewCode)}>
                  <div>{displayName(col.roleName).split(' ').map((w, i) => <div key={i}>{w}</div>)}</div>
                </th>
              );
            })}
          </tr>
          <tr>
            <th style={{ ...headerCell('#fff'), textAlign: 'left', position: 'sticky', left: 0, zIndex: 3, top: 34 }} />
            {(() => { lastCode = null; return null; })()}
            {columns.map(col => {
              const s = roleStyle[col.roleCode] || roleStyle.A;
              return (
                <th key={col.roleSort} style={{ ...headerCell(s.bg, s.text), top: 34, fontSize: 9, fontWeight: 400 }}>
                  {col.roleSublevel}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {competencyGroups.map(group => (
            <React.Fragment key={group.name}>
              <tr>
                <td style={groupLabelCell}>{group.name}</td>
                {columns.map(col => (
                  <td key={col.roleSort} style={{ background: '#fff', borderLeft: '1px solid rgba(0,0,0,0.05)' }} />
                ))}
              </tr>
              {group.competencies.map(comp => (
                <tr key={comp.key}>
                  <td style={labelCell}>{comp.label}</td>
                  {columns.map(col => {
                    const grade = col.competencies[comp.key] || 0;
                    const s = roleStyle[col.roleCode] || roleStyle.A;
                    return (
                      <td key={col.roleSort} style={{
                        padding: '4px 6px',
                        textAlign: 'center',
                        fontSize: 10,
                        fontFamily: FONT,
                        background: '#fff',
                        borderLeft: '1px solid rgba(0,0,0,0.05)',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <CompetencyLevelIndicator grade={grade} size="small" />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Growth path bar (used in My Role tab)
// ─────────────────────────────────────────────────────────────────────────────

function GrowthPath({ currentRow, roleGroups, roleStyle }) {
  const currentCode = currentRow?.roleCode;

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, minWidth: 'max-content', padding: '4px 0' }}>
        {roleGroups.map((group, idx) => {
          const code = group.roleCode;
          const s = roleStyle[code] || roleStyle.A;
          const isCurrent = code === currentCode;
          const isPast = currentCode && roleGroups.findIndex(g => g.roleCode === code) < roleGroups.findIndex(g => g.roleCode === currentCode);
          const names = { A: 'Intern', B: 'Junior', C: 'Medior', D: 'Project Arch.', E: 'Senior', F: 'Associate' };
          const circleSize = isCurrent ? 40 : 32;

          return (
            <React.Fragment key={code}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, paddingTop: isCurrent ? 0 : 4 }}>
                <div style={{
                  width: circleSize, height: circleSize,
                  borderRadius: '50%',
                  background: isCurrent ? s.chip : isPast ? `${s.chip}55` : '#e8e8e8',
                  border: `2px solid ${isCurrent ? s.chip : isPast ? `${s.chip}88` : '#d0d0d0'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT, fontWeight: 700,
                  fontSize: isCurrent ? 15 : 12,
                  color: isCurrent ? '#fff' : isPast ? s.chip : '#aaa',
                  transition: 'all 0.2s',
                  boxShadow: isCurrent ? `0 0 0 3px ${s.chip}33` : 'none',
                }}>
                  {code}
                </div>
                <div style={{
                  fontSize: 9, fontFamily: FONT, fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent ? s.text : '#999',
                  textAlign: 'center', maxWidth: 64,
                }}>
                  {names[code] || displayName(group.roleName)}
                  {isCurrent && currentRow && (
                    <div style={{ fontSize: 8, color: s.chip, fontWeight: 600 }}>
                      {currentRow.roleSublevel}
                    </div>
                  )}
                </div>
              </div>
              {idx < roleGroups.length - 1 && (
                <div style={{
                  width: 28, height: 2,
                  background: isPast ? `${s.chip}55` : 'rgba(0,0,0,0.1)',
                  margin: '0 2px',
                  marginTop: 20,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab C: My Role — personalised view
// ─────────────────────────────────────────────────────────────────────────────

function MyRoleTab({ currentUser, roleGroups, competencyGroups, roleStyle, matchUserToRole, getAdjacentRows }) {
  const jobRole  = currentUser?.effectiveJobRole  ?? currentUser?.jobRole;
  const jobLevel = currentUser?.effectiveJobLevel ?? currentUser?.jobLevel;
  const nlSchaal = currentUser?.effectiveNlSchaal ?? null;

  const matched  = matchUserToRole(jobRole, jobLevel, nlSchaal);
  const adjacent = getAdjacentRows(matched);

  const exactMatch = matched && jobLevel && matched.roleSublevel === jobLevel;
  const roleOnlyMatch = matched && !exactMatch;

  // ── Empty state ──
  if (!jobRole && !jobLevel) {
    return (
      <BrioAnalyticsSurface padding="m">
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <User size={32} color="#ccc" style={{ marginBottom: 12 }} />
          <div style={{ fontFamily: FONT, fontSize: 14, color: '#999', marginBottom: 6 }}>
            Your role has not been set yet.
          </div>
          <div style={{ fontFamily: FONT, fontSize: 12, color: '#bbb' }}>
            An admin can update your profile with your current role and level.
          </div>
        </div>
      </BrioAnalyticsSurface>
    );
  }

  if (!matched) {
    return (
      <BrioAnalyticsSurface padding="m">
        <div style={{ textAlign: 'center', padding: '24px 16px' }}>
          <div style={{ fontFamily: FONT, fontSize: 13, color: '#999', marginBottom: 6 }}>
            Role not found in the framework
          </div>
          <div style={{ fontFamily: FONT, fontSize: 11, color: '#bbb' }}>
            Your profile shows <strong>{jobRole}{jobLevel ? ` / ${jobLevel}` : ''}</strong>,
            which doesn&apos;t match a current framework entry.
          </div>
        </div>
      </BrioAnalyticsSurface>
    );
  }

  const s = roleStyle[matched.roleCode] || roleStyle.A;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Growth path overview */}
      <BrioAnalyticsSurface padding="m">
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontFamily: FONT, fontSize: 11, color: '#888', marginBottom: 8 }}>
            Your path in the BRIO role framework
          </div>
          <GrowthPath currentRow={matched} roleGroups={roleGroups} roleStyle={roleStyle} />
        </div>
      </BrioAnalyticsSurface>

      {/* Role-only match notice */}
      {roleOnlyMatch && (
        <div style={{
          padding: '10px 14px',
          background: 'rgba(159,144,42,0.08)',
          border: '1px solid rgba(159,144,42,0.25)',
          borderRadius: 8,
          fontFamily: FONT, fontSize: 11, color: '#5a4e10',
        }}>
          Your exact sublevel hasn&apos;t been set yet, so this view shows the broader <strong>{displayName(matched.roleName)}</strong> role expectations.
        </div>
      )}

      {/* Position statement */}
      <BrioAnalyticsSurface padding="m">
        <div style={{ marginBottom: 6, fontSize: 11, color: '#888', fontFamily: FONT }}>
          You are currently positioned at
        </div>
        <RoleBadge roleCode={matched.roleCode} roleName={matched.roleName} sublevel={matched.roleSublevel} roleStyle={roleStyle} />
        {matched.experienceRange && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#777', fontFamily: FONT }}>
            Typical experience range: <strong>{matched.experienceRange} years</strong>
            {matched.bonusEligible ? ' · Bonus eligible' : ''}
          </div>
        )}
        <RoleIntroduction intro={ROLE_INTRODUCTIONS[matched.roleName]} compact />
      </BrioAnalyticsSurface>

      {/* Current role card */}
      <div>
        <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 8 }}>
          What is expected in this role
        </div>
        <SublevelCard row={matched} highlight label="Current" competencyGroups={competencyGroups} roleStyle={roleStyle} />
      </div>

      {/* Responsibilities and growth */}
      <BrioAnalyticsSurface padding="m">
        <SectionLabel>Typical responsibilities in this role include</SectionLabel>
        <p style={{ fontFamily: FONT, fontSize: 12, color: '#333', lineHeight: 1.7, margin: 0 }}>
          {matched.coreResponsibilities}
        </p>

        <div style={{ marginTop: 14 }}>
          <SectionLabel>Where people in this role often deepen over time</SectionLabel>
          <p style={{ fontFamily: FONT, fontSize: 12, color: '#555', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
            {matched.growthFocus}
          </p>
        </div>

        {/* Competency highlights — now graded */}
        <div style={{ marginTop: 14 }}>
          <SectionLabel>Key competencies at this stage</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {competencyGroups.flatMap(g => g.competencies)
              .filter(comp => (matched.competencies[comp.key] || 0) > 0)
              .map(comp => {
                const grade = matched.competencies[comp.key];
                return (
                  <span key={comp.key} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 10, fontFamily: FONT,
                    padding: '3px 9px', borderRadius: 12,
                    background: s.bg, border: `1px solid ${s.border}`, color: s.text,
                  }}>
                    <CompetencyLevelIndicator grade={grade} size="small" />
                    {comp.label}
                  </span>
                );
              })
            }
          </div>
        </div>
      </BrioAnalyticsSurface>

      {/* Adjacent rows */}
      {(adjacent.prev || adjacent.next) && (
        <div>
          <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 10 }}>
            Context around your current position
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {adjacent.prev && (
              <SublevelCard row={adjacent.prev} label="Earlier stage in this path" competencyGroups={competencyGroups} roleStyle={roleStyle} />
            )}
            {adjacent.next && (
              <div>
                <div style={{ fontFamily: FONT, fontSize: 11, color: '#777', marginBottom: 6, paddingLeft: 2 }}>
                  A possible next step, when relevant, could be:
                </div>
                <SublevelCard row={adjacent.next} label="Possible next step over time" competencyGroups={competencyGroups} roleStyle={roleStyle} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Closing note */}
      <div style={{
        padding: '10px 14px',
        background: 'rgba(0,0,0,0.02)',
        border: '1px solid rgba(0,0,0,0.07)',
        borderRadius: 8,
        fontFamily: FONT, fontSize: 11, color: '#888', lineHeight: 1.6,
      }}>
        This view is informational and reflects the BRIO role framework as it stands today.
        Growth paths evolve naturally — this overview is here to give you a clear, honest
        picture of where you are and what the broader landscape looks like.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────────────────

export default function RoleFrameworkPage({ currentUser }) {
  const [activeTab, setActiveTab] = useState('overview');
  const {
    rows, roleGroups, competencyGroups, roleStyle,
    loading, error,
    matchUserToRole: matchFn, getAdjacentRows: adjFn,
  } = useRoleFramework();

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'matrix',   label: 'Competency Matrix' },
    { id: 'my-role',  label: 'My Role' },
  ];

  if (loading) {
    return (
      <div className="brio-analytics-font" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <BrioFrame>
          <div style={{ padding: 'var(--brio-space-m)', textAlign: 'center' }}>
            <div style={{ fontFamily: FONT, fontSize: 13, color: '#999' }}>Loading role framework...</div>
          </div>
        </BrioFrame>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brio-analytics-font" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <BrioFrame>
          <div style={{ padding: 'var(--brio-space-m)', textAlign: 'center' }}>
            <div style={{ fontFamily: FONT, fontSize: 13, color: '#c00' }}>Failed to load role framework: {error}</div>
          </div>
        </BrioFrame>
      </div>
    );
  }

  return (
    <div className="brio-analytics-font" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <BrioFrame>
        <div style={{ paddingTop: 'var(--brio-space-m)', paddingBottom: 'var(--brio-space-m)' }}>

          {/* Header */}
          <BrioAnalyticsSurface padding="m">
            <div style={{ marginBottom: 'var(--brio-space-xs)' }}>
              <div style={{
                fontSize: 'var(--brio-font-size-base)', color: 'var(--brio-grey)',
                fontFamily: 'var(--brio-font-family)', fontWeight: 300,
              }}>
                BRIO role framework · {rows.length} role levels across {roleGroups.length} roles
              </div>
            </div>

            {/* Tab selector */}
            <div style={{ display: 'flex', gap: 'var(--brio-space-xs)', flexWrap: 'wrap' }}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`brio-btn brio-btn--sm ${activeTab === tab.id ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </BrioAnalyticsSurface>

          {/* Tab content */}
          <div style={{ marginTop: 14 }}>
            {activeTab === 'overview' && (
              <OverviewTab roleGroups={roleGroups} competencyGroups={competencyGroups} roleStyle={roleStyle} />
            )}
            {activeTab === 'matrix' && (
              <BrioAnalyticsSurface padding="m">
                <CompetencyLegend />
                <div style={{ marginBottom: 10, fontSize: 11, color: '#888', fontFamily: FONT }}>
                  Scroll right to see all role levels. Segments indicate competency grade (0–3).
                </div>
                <CompetencyMatrixTab rows={rows} competencyGroups={competencyGroups} roleStyle={roleStyle} />
              </BrioAnalyticsSurface>
            )}
            {activeTab === 'my-role' && (
              <MyRoleTab
                currentUser={currentUser}
                roleGroups={roleGroups}
                competencyGroups={competencyGroups}
                roleStyle={roleStyle}
                matchUserToRole={matchFn}
                getAdjacentRows={adjFn}
              />
            )}
          </div>

        </div>
      </BrioFrame>
    </div>
  );
}
